import { Country } from '@afterpay/types'
import { useModal } from '@afterpaytouch/core'
import { Route } from '../../config/router'
import { PaymentOption, PaymentOptionsErrorModalKey, paymentOptionsErrors, TerminalErrorCode } from '../../model'
import { portalApiErrorFromPortalThunkRejectedValue } from '../../model/errors'
import {
  setCheckoutError,
  setPaymentOptionsError,
  setShowEfaModal,
  useAppDispatch,
  useAppRouter,
  useCountry,
  useCreditFreezeRetryContext,
  usePBIRejected,
  usePaymentOptionsError,
  useSelectedPaymentOption,
} from '../../state'
import { useState } from 'react'
import { FetchVerificationErrorResponse } from '@afterpaytouch/event-verify'
import { ErrorCode } from '@afterpaytouch/portal-api'

interface PaymentOptionsErrorResponse {
  errorCode: ErrorCode
  errorInfo?: {
    sub_reason?: string
    maximum_order_value?: string
    credit_freeze_application_deadline?: string
  }
}

interface IUseHandlePaymentOptionsError {
  handlePaymentOptionsError: (e: unknown) => void
}

interface IUseSendEFARobocallEvent {
  onSuccess: () => void
  onError: (d: FetchVerificationErrorResponse) => void
  isSuccess: boolean
  isDecline: boolean
}

export const useHandlePaymentOptionsError = (): IUseHandlePaymentOptionsError => {
  const dispatch = useAppDispatch()
  const isPBIRejected = usePBIRejected()
  const router = useAppRouter()
  const country = useCountry()
  const currentPaymentOptionsError = usePaymentOptionsError()
  // After `resetPaymentOptionsForRetry` clears `state.error` (so the PCL radio and route effects
  // unblock), this context carries the prior credit-freeze deadline forward so a 429 during the
  // retry fetch can still route back to RETRY_CREDIT_FREEZE with the original deadline.
  const creditFreezeRetryContext = useCreditFreezeRetryContext()
  const { showModal: showPaymentOptionsErrorModal } = useModal(PaymentOptionsErrorModalKey)

  const handlePaymentOptionsError = (e: unknown): void => {
    const portalError = portalApiErrorFromPortalThunkRejectedValue(e) ?? (e as PaymentOptionsErrorResponse)
    // Portal `ErrorInfo` typings omit payment-options-only fields (e.g. sub_reason); runtime responses include them.
    const errorInfo = portalError.errorInfo as PaymentOptionsErrorResponse['errorInfo'] | undefined
    const errorKey = paymentOptionsErrors[portalError.errorCode] ?? TerminalErrorCode.ErrorPCL
    const subReason = errorKey === TerminalErrorCode.RiskDeclinedPCL ? errorInfo?.sub_reason : undefined
    const subErrorKey = subReason === 'credit_freeze' ? TerminalErrorCode.CreditFreezeDeclinedPCL : errorKey
    const maximumOrderValue = errorKey === TerminalErrorCode.AmountTooHighDeclinePCL ? errorInfo?.maximum_order_value : undefined
    const creditFreezeApplicationDeadline = errorInfo?.credit_freeze_application_deadline

    if (errorKey === TerminalErrorCode.ExtendedFraudAlert) {
      dispatch(setShowEfaModal(true))

      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
        })
      )
      return
    }

    const isCreditFreezeRetry429 =
      portalError.errorCode === ErrorCode.TooManyRequests &&
      (currentPaymentOptionsError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL || creditFreezeRetryContext != null)

    if (isCreditFreezeRetry429) {
      // Route a rate-limited credit-freeze retry back to RETRY_CREDIT_FREEZE with the deadline
      // preserved across `resetPaymentOptionsForRetry` (either still on `state.error` if the user
      // hasn't yet clicked retry, or in `creditFreezeRetryContext` after retry cleared the error).
      const preservedDeadline = currentPaymentOptionsError?.extra?.creditFreezeApplicationDeadline ?? creditFreezeRetryContext?.deadline
      const preservedErrorCode = currentPaymentOptionsError?.errorCode ?? creditFreezeRetryContext?.errorCode ?? portalError.errorCode
      dispatch(
        setPaymentOptionsError({
          error: TerminalErrorCode.CreditFreezeDeclinedPCL,
          errorCode: preservedErrorCode,
          extra: {
            ...currentPaymentOptionsError?.extra,
            creditFreezeApplicationDeadline: preservedDeadline,
            tooManyRequests: true,
          },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (subErrorKey === TerminalErrorCode.CreditFreezeDeclinedPCL && creditFreezeApplicationDeadline != null && creditFreezeApplicationDeadline !== '') {
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          ...{ extra: { creditFreezeApplicationDeadline, tooManyRequests: false } },
        })
      )
      router.push(Route.RETRY_CREDIT_FREEZE)
      return
    }

    if (isPBIRejected) {
      dispatch(
        setCheckoutError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          extra: {
            maximumOrderValue,
          },
        })
      )

      router.push(Route.TERMINAL_ERROR)
    } else {
      // show paymentOptions modal in US only?
      if (country !== Country.AU) {
        showPaymentOptionsErrorModal()
      }
      dispatch(
        setPaymentOptionsError({
          error: subErrorKey,
          errorCode: portalError.errorCode,
          ...(maximumOrderValue !== undefined && { extra: { maximumOrderValue } }),
        })
      )
    }
  }
  return { handlePaymentOptionsError }
}

export const useSendEFARobocallEvent = (): IUseSendEFARobocallEvent => {
  const [efaRobocallSuccess, setEfaRobocallSuccess] = useState(false)
  const [efaRobocallDecline, setEfaRobocallDecline] = useState(false)

  const onSuccess = (): void => {
    setEfaRobocallSuccess(true)
    setEfaRobocallDecline(false)
  }
  const onError = (e: FetchVerificationErrorResponse): void => {
    setEfaRobocallSuccess(false)
    const error = e.message?.toLowerCase()
    // if it is fraud in efa flow, backend returns "Declined" as error data
    if (error === 'declined') {
      setEfaRobocallDecline(true)
    }
  }

  const isSuccess = efaRobocallSuccess ?? false
  const isDecline = efaRobocallDecline ?? false
  return {
    onSuccess,
    onError,
    isSuccess,
    isDecline,
  }
}

// This function is used to store the selected payment option of the UI state.
export const useSelectedPaymentOptionState = (): [PaymentOption | undefined, (paymentOption: PaymentOption) => void] => {
  const selectedPaymentOption = useSelectedPaymentOption()
  const [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal] = useState(selectedPaymentOption)

  return [selectedPaymentOptionLocal, setSelectedPaymentOptionLocal]
}
